import type {
  AddressValidationComponent,
  AddressValidationConfirmationLevel,
  AddressValidationResult
} from "src/state/ShippingAddress/addressValidationTypeAliases";
import { ShippingAddressData } from "src/state/ShippingAddress/ShippingAddressData";
import { Validation2AddressSuggestion } from "src/state/ShippingAddress/Validation2AddressSuggestion";

const getComponentText = (
  components: AddressValidationComponent[],
  type: string
): string | undefined =>
  components.find((c) => c.componentType === type)?.componentName?.text ??
  undefined;

/**
 * Any suggested address with weight higher than this limit should cause
 * a suggestion, even if the suggestion doesn't differ from the original
 * address.
 */
export const ADDRESS_SUGGESTION_WEIGHT_LIMIT = 10;

/**
 * Transforms a Google Address Validation API result into a ShippingAddressData
 * object by extracting relevant address components.
 *
 * Only address-related fields are mapped (street, aptSuite, city, state, zip).
 * Name fields (firstName, lastName) are not part of the validation result and
 * will not be present in the output.
 */
export const extractConfirmationLevel = (
  result: AddressValidationResult
): Record<
  string,
  NonNullable<AddressValidationComponent["confirmationLevel"]>
> => {
  const components = result.address?.addressComponents ?? [];

  return Object.fromEntries(
    components
      .filter(
        (
          c
        ): c is typeof c & {
          componentType: string;
          confirmationLevel: NonNullable<typeof c.confirmationLevel>;
        } => c.componentType != null && c.confirmationLevel != null
      )
      .map((c) => [c.componentType, c.confirmationLevel])
  );
};

const addressComponentTypes = [
  "street_number",
  "route",
  "subpremise",
  "locality",
  "administrative_area_level_1",
  "postal_code"
] as const;

const confirmationWeight: {
  [key in AddressValidationConfirmationLevel]: number;
} = {
  0: Number.MAX_SAFE_INTEGER,
  1: 0,
  2: 5,
  3: ADDRESS_SUGGESTION_WEIGHT_LIMIT + 10,
  CONFIRMED: 0,
  UNCONFIRMED_BUT_PLAUSIBLE: 5,
  UNCONFIRMED_AND_SUSPICIOUS: ADDRESS_SUGGESTION_WEIGHT_LIMIT + 10,
  CONFIRMATION_LEVEL_UNSPECIFIED: Number.MAX_SAFE_INTEGER
};

export const getAddressConfirmationWeight = (
  levelsByComponent: Record<string, AddressValidationConfirmationLevel>
): number => {
  const levels = addressComponentTypes
    .filter((type) => type in levelsByComponent)
    .map((type) => levelsByComponent[type]);

  if (levels.length === 0) {
    return Number.MAX_SAFE_INTEGER;
  }

  return levels.reduce<number>(
    (worst, current) => Math.max(confirmationWeight[current], worst),
    0
  );
};

export const validationResult2Address = ({
  result,
  fallback
}: {
  result: AddressValidationResult;
  fallback: ShippingAddressData;
}): Validation2AddressSuggestion => {
  const components = result.address?.addressComponents ?? [];

  const streetNumber = getComponentText(components, "street_number");
  const streetName = getComponentText(components, "route");
  const aptSuite =
    getComponentText(components, "subpremise") ?? fallback.aptSuite ?? "";
  const city = getComponentText(components, "locality") ?? fallback.city ?? "";
  const state =
    getComponentText(components, "administrative_area_level_1") ??
    fallback.state ??
    "";
  const zip = getComponentText(components, "postal_code") ?? fallback.zip ?? "";

  const street =
    `${streetNumber ?? ""} ${streetName ?? ""}`.trim() || fallback.street;

  const suggestedAddress: ShippingAddressData = {
    street,
    aptSuite,
    city,
    state,
    zip,
    firstName: fallback.firstName,
    lastName: fallback.lastName
  };

  const differsFromFallback =
    Object.keys(fallback).length !== Object.keys(suggestedAddress).length ||
    Object.entries(fallback).reduce(
      (res, [key, value]) =>
        res || suggestedAddress[key as keyof ShippingAddressData] !== value,
      false
    );

  return {
    suggestedAddress,
    differsFromFallback
  };
};
